import { Language, languageNames } from '@/context/languages/languageData';

export interface HardcodedContent {
  originalText: string;
  context: string;
  filePath: string;
  lineNumber: number;
  category: 'platform_features' | 'statistics' | 'services' | 'testimonials' | 'faq' | 'cta' | 'security' | 'process_steps';
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface TranslationMapping {
  [key: string]: Partial<Record<Language, string>>;
}

export class HardcodedContentDetector {
  
  // Comprehensive list of hardcoded strings that need translation
  private static readonly HARDCODED_STRINGS: HardcodedContent[] = [
    // Platform Features
    { originalText: 'Why Choose Platform', context: 'Platform benefits section', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Trusted By Millions', context: 'Trust indicator', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Active Professionals', context: 'Statistics label', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Countries Covered', context: 'Geographic coverage', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Sessions Completed', context: 'Success metrics', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Average Rating', context: 'Quality indicator', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Global Coverage', context: 'Coverage description', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Countries Plus', context: 'Coverage indicator', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Connect Professionals Language', context: 'Language support', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'All Therapists Certified', context: 'Quality assurance', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Security Features
    { originalText: 'Secure Video Sessions', context: 'Security feature', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Bank Grade Security', context: 'Security level', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Hipaa Compliant Encrypted', context: 'Compliance indicator', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Flexible Scheduling', context: 'Booking feature', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Same Day Available', context: 'Availability feature', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Book Sessions Schedule', context: 'Booking action', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },

    // Matching & Algorithm
    { originalText: 'Personalized Matching', context: 'Matching feature', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Success Rate', context: 'Algorithm effectiveness', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Advanced Algorithm Matches', context: 'Matching technology', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Multiple Languages', context: 'Language support', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Languages Plus', context: 'Language count', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Therapy Sessions Languages', context: 'Session language support', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Compliance & Certification
    { originalText: 'Hipaa Compliant', context: 'Privacy compliance', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Iso Certified', context: 'Quality certification', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'high' },
    { originalText: 'Rating Four Nine', context: 'Quality rating', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Endorsed By Dr Marin', context: 'Expert endorsement', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Process Steps
    { originalText: 'How It Works', context: 'Process section title', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'How It Works Description', context: 'Process explanation', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Find Your Therapist', context: 'Step 1', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Search By Location Specialty', context: 'Step 1 description', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Book Your Session', context: 'Step 2', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Select Convenient Time', context: 'Step 2 description', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Connect Securely', context: 'Step 3', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Join Session Securely', context: 'Step 3 description', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Continue Your Journey', context: 'Step 4', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },
    { originalText: 'Schedule Follow Up Sessions', context: 'Step 4 description', filePath: 'src/components/', lineNumber: 0, category: 'process_steps', priority: 'critical' },

    // Global Coverage
    { originalText: 'Global Mental Health Coverage', context: 'Coverage section', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Global Coverage Description', context: 'Coverage explanation', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Find Therapists By Country', context: 'Country navigation', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Major Cities With Mental Health Services', context: 'City services', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Specialized Mental Health Services', context: 'Service categories', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },

    // Service Types with Professional Counts
    { originalText: 'Anxiety Therapy', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Depression Treatment', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Couples Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Child Psychology', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'PTSD Treatment', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Addiction Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Online Therapy', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'CBT Therapy', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Family Therapy', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Trauma Therapy', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Grief Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Stress Management', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Relationship Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Teen Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },
    { originalText: 'Marriage Counseling', context: 'Service type', filePath: 'src/components/', lineNumber: 0, category: 'services', priority: 'critical' },

    // Navigation & Actions
    { originalText: "Can't find your location? We're constantly expanding our network.", context: 'Location expansion notice', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'medium' },
    { originalText: 'Browse All Locations', context: 'Navigation link', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },

    // Testimonials Section
    { originalText: 'What Our Clients Say', context: 'Testimonials section', filePath: 'src/components/', lineNumber: 0, category: 'testimonials', priority: 'critical' },
    { originalText: 'Real stories from people who found the right therapist, psychologist, or psychotherapist for their journey.', context: 'Testimonials description', filePath: 'src/components/', lineNumber: 0, category: 'testimonials', priority: 'critical' },

    // Availability Section
    { originalText: 'Available Online Now', context: 'Availability indicator', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Connect instantly with therapists ready to help in your preferred language', context: 'Instant connection', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Featured Therapists', context: 'Therapist showcase', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Connect with our top-rated mental health professionals who are ready to help you on your journey to wellness.', context: 'Featured description', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Quality Indicators
    { originalText: 'Top Rated', context: 'Quality badge', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Verified Professionals', context: 'Verification badge', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Available Now', context: 'Availability badge', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'high' },
    { originalText: 'Trusted by Thousands Worldwide', context: 'Trust statement', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Our commitment to quality and safety has earned the trust of patients and professionals across the globe', context: 'Trust description', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Statistics Labels
    { originalText: 'Patients Served', context: 'Statistic label', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Successful therapy connections worldwide', context: 'Statistic description', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Global mental health coverage', context: 'Coverage description', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Based on verified patient reviews', context: 'Rating source', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'All therapists certified through our program', context: 'Certification note', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Security & Privacy
    { originalText: 'Your Security & Privacy Are Our Priority', context: 'Security section title', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'We implement the highest standards of security and privacy protection to ensure your mental health journey remains confidential and safe', context: 'Security description', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'End-to-End Encryption', context: 'Security feature', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'All communications are encrypted with military-grade security', context: 'Encryption description', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Full compliance with healthcare privacy regulations', context: 'Compliance description', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Identity Verification', context: 'Verification feature', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Rigorous verification process for all mental health professionals', context: 'Verification description', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: 'Crisis Support', context: 'Support feature', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },
    { originalText: '24/7 emergency support and crisis intervention protocols', context: 'Crisis support description', filePath: 'src/components/', lineNumber: 0, category: 'security', priority: 'critical' },

    // Professional Accreditations
    { originalText: 'Professional Accreditations & Memberships', context: 'Accreditation section', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },
    { originalText: 'Our platform is recognized and accredited by leading mental health organizations worldwide', context: 'Accreditation description', filePath: 'src/components/', lineNumber: 0, category: 'platform_features', priority: 'critical' },

    // Reviews Section
    { originalText: '4.9/5 Stars from Verified Patients', context: 'Review summary', filePath: 'src/components/', lineNumber: 0, category: 'testimonials', priority: 'critical' },
    { originalText: 'Based on 2,847+ verified reviews from patients worldwide', context: 'Review source', filePath: 'src/components/', lineNumber: 0, category: 'testimonials', priority: 'critical' },
    { originalText: 'Read All Reviews', context: 'Review action', filePath: 'src/components/', lineNumber: 0, category: 'testimonials', priority: 'medium' },

    // FAQ Section
    { originalText: 'Frequently Asked Questions', context: 'FAQ section title', filePath: 'src/components/', lineNumber: 0, category: 'faq', priority: 'critical' },
    { originalText: 'Find answers to common questions about our mental health platform and services', context: 'FAQ description', filePath: 'src/components/', lineNumber: 0, category: 'faq', priority: 'critical' },

    // CTA Section
    { originalText: 'Start Your Journey Today', context: 'Main CTA', filePath: 'src/components/', lineNumber: 0, category: 'cta', priority: 'critical' },
    { originalText: 'Cta Description', context: 'CTA description', filePath: 'src/components/', lineNumber: 0, category: 'cta', priority: 'critical' },
    { originalText: 'Find A Therapist', context: 'Primary CTA button', filePath: 'src/components/', lineNumber: 0, category: 'cta', priority: 'critical' },
    { originalText: 'Learn How It Works', context: 'Secondary CTA button', filePath: 'src/components/', lineNumber: 0, category: 'cta', priority: 'critical' },

    // Footer Statistics
    { originalText: 'Profissionais Verificados', context: 'Professional count (Portuguese)', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Países', context: 'Country count (Portuguese)', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' },
    { originalText: 'Successful Sessions', context: 'Session count', filePath: 'src/components/', lineNumber: 0, category: 'statistics', priority: 'high' }
  ];

  /**
   * Get comprehensive translation mappings for all hardcoded content
   */
  static getTranslationMappings(): TranslationMapping {
    const translations: TranslationMapping = {};

    // Generate translations for each hardcoded string
    this.HARDCODED_STRINGS.forEach(item => {
      translations[item.originalText] = this.generateTranslationsForString(item.originalText, item.context);
    });

    return translations;
  }

  /**
   * Generate translations for a specific string across all languages
   */
  private static generateTranslationsForString(text: string, context: string): Partial<Record<Language, string>> {
    const translations = {} as Partial<Record<Language, string>>;

    // Define translations for each language
    const translationMap: { [key: string]: Partial<Record<Language, string>> } = {
      'Why Choose Platform': {
        en: 'Why Choose Platform',
        es: '¿Por Qué Elegir Nuestra Plataforma?',
        fr: 'Pourquoi Choisir Notre Plateforme',
        de: 'Warum Unsere Plattform Wählen',
        it: 'Perché Scegliere La Nostra Piattaforma',
        pt: 'Por Que Escolher Nossa Plataforma',
        ko: '플랫폼을 선택하는 이유',
        ja: 'プラットフォームを選ぶ理由',
        zh: '为什么选择我们的平台',
        ar: 'لماذا تختار منصتنا',
        hi: 'हमारा प्लेटफॉर्म क्यों चुनें',
        ru: 'Почему выбрать нашу платформу',
        tr: 'Neden Platformumuzu Seçmelisiniz',
        th: 'ทำไมต้องเลือกแพลตฟอร์มของเรา',
        vi: 'Tại Sao Chọn Nền Tảng Của Chúng Tôi',
        id: 'Mengapa Memilih Platform Kami',
        nl: 'Waarom Ons Platform Kiezen',
        ur: 'ہمارا پلیٹ فارم کیوں منتخب کریں',
        bn: 'কেন আমাদের প্ল্যাটফর্ম বেছে নিন',
        fa: 'چرا پلتفرم ما را انتخاب کنید',
        ms: 'Mengapa Pilih Platform Kami'
      },
      
      'Trusted By Millions': {
        en: 'Trusted By Millions',
        es: 'Confiado por Millones',
        fr: 'Fait Confiance par des Millions',
        de: 'Von Millionen Vertraut',
        it: 'Fidato da Milioni',
        pt: 'Confiado por Milhões',
        ko: '수백만 명이 신뢰',
        ja: '何百万人に信頼される',
        zh: '数百万人信赖',
        ar: 'موثوق من قبل الملايين',
        hi: 'लाखों लोगों द्वारा भरोसा',
        ru: 'Доверяют миллионы',
        tr: 'Milyonlarca Kişi Tarafından Güvenilir',
        th: 'ได้รับความไว้วางใจจากผู้คนนับล้าน',
        vi: 'Được Hàng Triệu Người Tin Tưởng',
        id: 'Dipercaya Jutaan Orang',
        nl: 'Vertrouwd Door Miljoenen',
        ur: 'لاکھوں لوگوں کا اعتماد',
        bn: 'লক্ষ লক্ষ মানুষের বিশ্বাস',
        fa: 'مورد اعتماد میلیون‌ها نفر',
        ms: 'Dipercayai Jutaan Orang'
      },

      'Active Professionals': {
        en: 'Active Professionals',
        es: 'Profesionales Activos',
        fr: 'Professionnels Actifs',
        de: 'Aktive Fachkräfte',
        it: 'Professionisti Attivi',
        pt: 'Profissionais Ativos',
        ko: '활동 중인 전문가',
        ja: 'アクティブ専門家',
        zh: '活跃专业人士',
        ar: 'المهنيون النشطون',
        hi: 'सक्रिय पेशेवर',
        ru: 'Активные специалисты',
        tr: 'Aktif Profesyoneller',
        th: 'ผู้เชี่ยวชาญที่ใช้งานอยู่',
        vi: 'Chuyên Gia Hoạt Động',
        id: 'Profesional Aktif',
        nl: 'Actieve Professionals',
        ur: 'فعال پیشہ ور افراد',
        bn: 'সক্রিয় পেশাদার',
        fa: 'متخصصان فعال',
        ms: 'Profesional Aktif'
      },

      'Global Coverage': {
        en: 'Global Coverage',
        es: 'Cobertura Global',
        fr: 'Couverture Mondiale',
        de: 'Weltweite Abdeckung',
        it: 'Copertura Globale',
        pt: 'Cobertura Global',
        ko: '글로벌 커버리지',
        ja: 'グローバルカバレッジ',
        zh: '全球覆盖',
        ar: 'تغطية عالمية',
        hi: 'वैश्विक कवरेज',
        ru: 'Глобальное покрытие',
        tr: 'Küresel Kapsama',
        th: 'ความครอบคลุมทั่วโลก',
        vi: 'Phủ Sóng Toàn Cầu',
        id: 'Jangkauan Global',
        nl: 'Wereldwijde Dekking',
        ur: 'عالمی کوریج',
        bn: 'বিশ্বব্যাপী কভারেজ',
        fa: 'پوشش جهانی',
        ms: 'Liputan Global'
      },

      'How It Works': {
        en: 'How It Works',
        es: 'Cómo Funciona',
        fr: 'Comment Ça Marche',
        de: 'So Funktioniert Es',
        it: 'Come Funziona',
        pt: 'Como Funciona',
        ko: '작동 방식',
        ja: '仕組み',
        zh: '如何运作',
        ar: 'كيف يعمل',
        hi: 'यह कैसे काम करता है',
        ru: 'Как это работает',
        tr: 'Nasıl Çalışır',
        th: 'วิธีการทำงาน',
        vi: 'Cách Thức Hoạt Động',
        id: 'Cara Kerja',
        nl: 'Hoe Het Werkt',
        ur: 'یہ کیسے کام کرتا ہے',
        bn: 'এটি কিভাবে কাজ করে',
        fa: 'نحوه کارکرد',
        ms: 'Bagaimana Ia Berfungsi'
      },

      'Find Your Therapist': {
        en: 'Find Your Therapist',
        es: 'Encuentra tu Terapeuta',
        fr: 'Trouvez Votre Thérapeute',
        de: 'Finden Sie Ihren Therapeuten',
        it: 'Trova il Tuo Terapeuta',
        pt: 'Encontre Seu Terapeuta',
        ko: '치료사 찾기',
        ja: 'セラピストを見つける',
        zh: '找到您的治疗师',
        ar: 'ابحث عن معالجك',
        hi: 'अपना चिकित्सक खोजें',
        ru: 'Найдите своего терапевта',
        tr: 'Terapistinizi Bulun',
        th: 'ค้นหานักบำบัดของคุณ',
        vi: 'Tìm Nhà Trị Liệu Của Bạn',
        id: 'Temukan Terapis Anda',
        nl: 'Vind Uw Therapeut',
        ur: 'اپنا معالج تلاش کریں',
        bn: 'আপনার থেরাপিস্ট খুঁজুন',
        fa: 'درمانگر خود را پیدا کنید',
        ms: 'Cari Ahli Terapi Anda'
      },

      'Anxiety Therapy': {
        en: 'Anxiety Therapy',
        es: 'Terapia para la Ansiedad',
        fr: 'Thérapie pour l\'Anxiété',
        de: 'Angsttherapie',
        it: 'Terapia per l\'Ansia',
        pt: 'Terapia para Ansiedade',
        ko: '불안 치료',
        ja: '不安療法',
        zh: '焦虑治疗',
        ar: 'علاج القلق',
        hi: 'चिंता चिकित्सा',
        ru: 'Терапия тревожности',
        tr: 'Anksiyete Terapisi',
        th: 'การบำบัดความวิตกกังวล',
        vi: 'Liệu Pháp Lo Âu',
        id: 'Terapi Kecemasan',
        nl: 'Angsttherapie',
        ur: 'اضطراب کا علاج',
        bn: 'উদ্বেগের চিকিৎসা',
        fa: 'درمان اضطراب',
        ms: 'Terapi Kebimbangan'
      },

      'Depression Treatment': {
        en: 'Depression Treatment',
        es: 'Tratamiento de la Depresión',
        fr: 'Traitement de la Dépression',
        de: 'Depressionsbehandlung',
        it: 'Trattamento della Depressione',
        pt: 'Tratamento da Depressão',
        ko: '우울증 치료',
        ja: 'うつ病治療',
        zh: '抑郁症治疗',
        ar: 'علاج الاكتئاب',
        hi: 'अवसाद का इलाज',
        ru: 'Лечение депрессии',
        tr: 'Depresyon Tedavisi',
        th: 'การรักษาซึมเศร้า',
        vi: 'Điều Trị Trầm Cảm',
        id: 'Pengobatan Depresi',
        nl: 'Depressiebehandeling',
        ur: 'ڈپریشن کا علاج',
        bn: 'বিষণ্নতার চিকিৎসা',
        fa: 'درمان افسردگی',
        ms: 'Rawatan Kemurungan'
      },

      'Start Your Journey Today': {
        en: 'Start Your Journey Today',
        es: 'Comienza tu Viaje Hoy',
        fr: 'Commencez Votre Voyage Aujourd\'hui',
        de: 'Beginnen Sie Ihre Reise Heute',
        it: 'Inizia il Tuo Viaggio Oggi',
        pt: 'Comece Sua Jornada Hoje',
        ko: '오늘 여행을 시작하세요',
        ja: '今日からあなたの旅を始めましょう',
        zh: '今天开始您的旅程',
        ar: 'ابدأ رحلتك اليوم',
        hi: 'आज अपनी यात्रा शुरू करें',
        ru: 'Начните свой путь сегодня',
        tr: 'Yolculuğunuza Bugün Başlayın',
        th: 'เริ่มต้นการเดินทางของคุณวันนี้',
        vi: 'Bắt Đầu Hành Trình Của Bạn Ngay Hôm Nay',
        id: 'Mulai Perjalanan Anda Hari Ini',
        nl: 'Begin Vandaag Uw Reis',
        ur: 'آج اپنا سفر شروع کریں',
        bn: 'আজই আপনার যাত্রা শুরু করুন',
        fa: 'سفر خود را امروز شروع کنید',
        ms: 'Mulakan Perjalanan Anda Hari Ini'
      }

      // Add more comprehensive translations as needed...
    };

    // Return the translation for this specific text, or generate a fallback
    if (translationMap[text]) {
      return translationMap[text];
    }

    // Fallback: basic translation attempt for unmapped strings
    return this.generateBasicTranslations(text);
  }

  /**
   * Generate basic translations for unmapped strings
   */
  private static generateBasicTranslations(text: string): Partial<Record<Language, string>> {
    // This is a simplified fallback - in practice you'd want proper translation service
    const basicTranslations = {} as Partial<Record<Language, string>>;
    
    Object.keys(languageNames).forEach(lang => {
      basicTranslations[lang as Language] = text; // Keep original as fallback
    });

    return basicTranslations;
  }

  /**
   * Get all hardcoded strings by category
   */
  static getHardcodedStringsByCategory(category: HardcodedContent['category']): HardcodedContent[] {
    return this.HARDCODED_STRINGS.filter(item => item.category === category);
  }

  /**
   * Get critical hardcoded strings that must be translated first
   */
  static getCriticalHardcodedStrings(): HardcodedContent[] {
    return this.HARDCODED_STRINGS.filter(item => item.priority === 'critical');
  }

  /**
   * Generate translation replacement map for a specific language
   */
  static getLanguageReplacements(language: Language): { [original: string]: string } {
    const replacements: { [original: string]: string } = {};
    const translations = this.getTranslationMappings();
    
    Object.keys(translations).forEach(original => {
      replacements[original] = translations[original][language];
    });

    return replacements;
  }
}